// w17 Mehrli — tender wedding catalog card (rose + ivory).
//
// Paper-invitation feel: an outer rose hairline frame with corner rosettes
// wraps an ivory inner card; a photo medallion sits centred with a rose
// matte, the couple's names render in Pinyon Script, and the bottom
// caption pairs the small-caps category with date + venue.
import { Photo, type LayoutCtx } from "./_shared";

export function MehrliPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  // Cooled rose mat so the inner card pops.
  const mat = `color-mix(in srgb, ${bg} 70%, ${accent} 30%)`;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: mat,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          position: "relative",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Corner rosettes — four tiny SVG flourishes pinning the page. */}
        {[
          { top: "4%", left: "4%", rotate: 0 },
          { top: "4%", right: "4%", rotate: 90 },
          { bottom: "4%", right: "4%", rotate: 180 },
          { bottom: "4%", left: "4%", rotate: 270 },
        ].map((pos, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              width: 16,
              height: 16,
              transform: `rotate(${pos.rotate}deg)`,
              top: pos.top,
              left: pos.left,
              right: pos.right,
              bottom: pos.bottom,
            }}
            aria-hidden
          >
            <svg viewBox="0 0 16 16" style={{ width: "100%", height: "100%" }}>
              <path d="M2 2 L8 2 M2 2 L2 8" stroke={accent} strokeWidth="0.5" fill="none" />
              <circle cx="3" cy="3" r="1.4" fill={accent} opacity="0.8" />
              <circle cx="3" cy="3" r="0.5" fill={bg} />
            </svg>
          </div>
        ))}

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TO'Y MAROSIMI
        </div>

        {/* Photo medallion with rose matte ring. */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${accent}, color-mix(in srgb, ${accent} 50%, #fff 50%), ${accent})`,
              padding: "5%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                padding: "4%",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Photo
                  src={photo}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  fallback={
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: `radial-gradient(circle at 50% 45%, ${accent}33, ${bg})`,
                      }}
                    />
                  }
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: `linear-gradient(180deg, transparent 55%, ${accent}22 100%)`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Names — Pinyon Script calligraphy. */}
        <div
          style={{
            fontFamily: font,
            fontSize: 32,
            lineHeight: 1.05,
            fontWeight: 400,
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TO'Y MAROSIMI · {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
